import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useCompany, useCompanyId } from './useCompany';
import { auth, companies } from '../../api/endpoints';
import { Button } from '../../components/Button';
import { PageHeader } from '../../components/Surface';
import { ConfirmByName } from '../../components/ConfirmByName';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { toast } from '../../components/Toast';
import { useT } from '../../i18n/provider';
import { useAuth } from '../../stores/auth';

/** `13 §2`: archiving a company — the destructive action of `13 §6`. */
export function DangerZoneScreen() {
  const t = useT();
  const id = useCompanyId();
  const company = useCompany();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [stopConfirming, setStopConfirming] = useState(false);

  const archive = useMutation({
    mutationFn: () => companies.archive(id),
    onSuccess: async () => {
      // The company list on the user object is what the navigation is built
      // from, so it has to be re-read before leaving this screen.
      useAuth.getState().setUser(await auth.me());
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      await navigate({ to: '/' });
    },
  });

  const restore = useMutation({
    mutationFn: () => companies.restore(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const stop = useMutation({
    mutationFn: () => companies.emergencyStop(id),
    onSuccess: async (result) => {
      setStopConfirming(false);
      toast.success(t('danger.stopped', { count: result.cancelled_runs }));
      await queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const resume = useMutation({
    mutationFn: () => companies.resume(id),
    onSuccess: async (result) => {
      toast.success(t('danger.resumed', { count: result.tasks_rescheduled }));
      await queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  if (company.isPending) {
    return <SkeletonRows rows={1} />;
  }

  const archived = company.data?.status === 'archived';
  const paused = company.data?.status === 'paused';

  return (
    <div className="max-w-lg space-y-4">
      <PageHeader title={t('company.tabs.danger')} description={t('danger.intro')} />

      {archive.error ? <ErrorBanner error={archive.error} /> : null}
      {restore.error ? <ErrorBanner error={restore.error} /> : null}
      {stop.error ? <ErrorBanner error={stop.error} /> : null}
      {resume.error ? <ErrorBanner error={resume.error} /> : null}

      {/* El botón del pánico va primero y no pide teclear el nombre: la
          confirmación de `13 §6` existe para que nadie archive por reflejo, y
          esto es lo contrario — una acción protectora, reversible, que se
          pulsa cuando algo se está yendo de las manos. Un paso de "¿seguro?"
          basta; teclear el nombre de la empresa mientras se quema dinero no. */}
      {!archived ? (
        <div className="rounded-lg border border-danger-line bg-danger-pale/40 p-5">
          <h2 className="text-sm font-semibold text-ink">
            {paused ? t('danger.pausedTitle') : t('danger.stopTitle')}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">
            {paused ? t('danger.pausedHint') : t('danger.stopHint')}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {paused ? (
              <Button busy={resume.isPending} onClick={() => resume.mutate()}>
                {t('danger.resume')}
              </Button>
            ) : stopConfirming ? (
              <>
                <Button variant="danger" busy={stop.isPending} onClick={() => stop.mutate()}>
                  {t('danger.stopConfirmYes')}
                </Button>
                <Button variant="secondary" onClick={() => setStopConfirming(false)}>
                  {t('common.cancel')}
                </Button>
                <span className="text-sm text-ink-soft">{t('danger.stopConfirm')}</span>
              </>
            ) : (
              <Button variant="danger" onClick={() => setStopConfirming(true)}>
                {t('danger.stop')}
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <div className="rounded-lg border border-danger-line bg-danger-pale/40 p-5">
        <h2 className="text-sm font-semibold text-ink">
          {archived ? t('danger.restoreTitle') : t('danger.archiveTitle')}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">
          {archived ? t('danger.restoreHint') : t('danger.archiveHint')}
        </p>

        <div className="mt-4">
          {archived ? (
            <Button variant="secondary" busy={restore.isPending} onClick={() => restore.mutate()}>
              {t('danger.restore')}
            </Button>
          ) : (
            <Button variant="danger" onClick={() => setConfirming(true)}>
              {t('danger.archive')}
            </Button>
          )}
        </div>
      </div>

      {confirming && company.data ? (
        <ConfirmByName
          name={company.data.name}
          verb={t('danger.archive')}
          description={t('danger.confirm')}
          busy={archive.isPending}
          onConfirm={() => archive.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
